import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Clock, CheckCircle2, XCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { verifyEmail } = useAuth();
  const isEs = language === 'es';

  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [errorMessage, setErrorMessage] = useState('');
  const [appName, setAppName] = useState('Regulisto');
  const ranOnce = useRef(false);

  useEffect(() => {
    if (ranOnce.current) return;
    ranOnce.current = true;

    const fetchAppName = async () => {
      try {
        const response = await axios.get(`${API_URL}/settings/public`);
        if (response.data?.app_name) setAppName(response.data.app_name);
      } catch (err) {
        console.error('Error fetching public settings:', err);
      }
    };
    fetchAppName();

    const doVerify = async () => {
      if (!token) {
        setStatus('error');
        setErrorMessage(isEs ? 'Enlace de confirmación no válido' : 'Invalid confirmation link');
        return;
      }
      const result = await verifyEmail(token);
      if (result.success) {
        setStatus('success');
        setTimeout(() => navigate('/dashboard'), 1500);
      } else {
        setStatus('error');
        setErrorMessage(result.error);
      }
    };
    doVerify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="verify-email-page">
      <Card className="w-full max-w-md shadow-lg border-slate-200 text-center">
        <CardHeader>
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold font-['Manrope'] text-slate-900">{appName}</span>
          </div>

          {status === 'verifying' && (
            <>
              <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <CardTitle className="text-xl">
                {isEs ? 'Confirmando tu cuenta...' : 'Confirming your account...'}
              </CardTitle>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <CardTitle className="text-xl">
                {isEs ? '¡Cuenta confirmada!' : 'Account confirmed!'}
              </CardTitle>
              <CardDescription>
                {isEs ? 'Redirigiendo a tu panel...' : 'Redirecting to your dashboard...'}
              </CardDescription>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <CardTitle className="text-xl">
                {isEs ? 'No se pudo confirmar' : 'Could not confirm'}
              </CardTitle>
              <CardDescription>{errorMessage}</CardDescription>
            </>
          )}
        </CardHeader>

        {status === 'error' && (
          <CardContent>
            <Link to="/login">
              <Button className="w-full bg-indigo-600 hover:bg-indigo-700">
                {isEs ? 'Volver al inicio de sesión' : 'Back to login'}
              </Button>
            </Link>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default VerifyEmail;
